/**
 * 損益試算（整合進「交易紀錄」用）。
 *
 * 真正整合：股數與買進價「沿用上方交易紀錄輸入的值」（受控 props），
 * 這裡只需再填一個「賣出價」就能算淨損益 / 報酬率 / 損益兩平 / 費用明細。
 * 不再各自重打股數與買進價。
 *
 * 台美股都用，依 isTW 切換手續費 / 稅規則。
 */

import { useEffect, useMemo, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { feeModelFor, roundTripFees, type FeeModel } from '../lib/fees';

export interface TradePlanLevels {
  entry: number;
  target1: number;
  target2: number;
  stopLoss: number;
  direction: 'long' | 'short';
}

function fmtMoney(n: number, isTW: boolean): string {
  if (isTW) return `NT$ ${Math.round(n).toLocaleString()}`;
  const abs = Math.abs(n);
  return `${n < 0 ? '-' : ''}$${abs.toFixed(2)}`;
}

/**
 * 來回手續費 + 稅 —— 統一走 shared 手續費模型（不折讓），與「模擬跟單組合」/ 後端結算同一套，
 * 避免同一檔在不同面板算出對不起來的手續費（見 packages/shared/src/fees.ts）。
 * 台股：買/賣 0.1425%（最低 NT$20）+ 賣出證交稅 0.3%（ETF 0.1%）。
 * 美股網路單：買/賣 0.08% + 賣出徵費（SEC/TAF）0.00206%。
 */
function computeFees(args: { model: FeeModel; entry: number; exit: number; shares: number }) {
  const { model, entry, exit, shares } = args;
  const f = roundTripFees(entry, exit, shares, model);
  // levy = 台股證交稅 / 美股賣出徵費（shared 模型已合一）。
  return { buyFee: f.buyFee, sellFee: f.sellFee, levy: f.levy, total: f.total };
}

export function ProfitCalc({ isTW, isEtf, shares, entryPrice, currentPrice, tradePlan, symbol }: {
  isTW: boolean;
  isEtf: boolean;
  /** 沿用「交易紀錄」的股數 */
  shares: number;
  /** 沿用「交易紀錄」的成交價當買進價 */
  entryPrice: number;
  /** 現價（賣出價 hint 用；無值時用 entryPrice） */
  currentPrice?: number;
  /** 有交易計畫時提供「賣在 T1 / T2 / 停損」快速套用 */
  tradePlan?: TradePlanLevels | null;
  /** 股票代號（給 shared feeModelFor 判台/美股）；無值時用 isTW fallback。 */
  symbol?: string;
}) {
  const hintPrice = currentPrice && currentPrice > 0 ? currentPrice : entryPrice;
  const [exitPrice, setExitPrice] = useState<number>(hintPrice);
  // shared 手續費模型：有 symbol 就用它判台/美股，否則用 isTW fallback（台股隨便選一檔）。
  const feeModel = useMemo<FeeModel>(
    () => feeModelFor(symbol ?? (isTW ? '2330.TW' : 'AAPL'), isEtf),
    [symbol, isTW, isEtf],
  );

  // 切標的（entryPrice 重新 seed）時，賣出價跟著回到現價，避免帶到上一檔
  useEffect(() => {
    setExitPrice(hintPrice > 0 ? hintPrice : entryPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryPrice]);

  const priceStep = isTW
    ? (exitPrice >= 100 ? 0.5 : exitPrice >= 50 ? 0.1 : exitPrice >= 10 ? 0.05 : 0.01)
    : 0.01;

  const fees = useMemo(
    () => computeFees({ model: feeModel, entry: entryPrice, exit: exitPrice, shares }),
    [feeModel, entryPrice, exitPrice, shares],
  );

  const buyCost = entryPrice * shares + fees.buyFee;
  const sellProceeds = exitPrice * shares - fees.sellFee - fees.levy;
  const netPnl = sellProceeds - buyCost;
  const netPct = buyCost > 0 ? (netPnl / buyCost) * 100 : 0;
  const breakEvenExit = useMemo(() => {
    if (shares <= 0 || entryPrice <= 0) return null;
    let lo = entryPrice * 0.5;
    let hi = entryPrice * 1.5;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      const f = computeFees({ model: feeModel, entry: entryPrice, exit: mid, shares });
      const pnl = mid * shares - f.sellFee - f.levy - (entryPrice * shares + f.buyFee);
      if (pnl > 0) hi = mid; else lo = mid;
    }
    return (lo + hi) / 2;
  }, [feeModel, entryPrice, shares]);

  const profitable = netPnl >= 0;
  const ready = shares > 0 && entryPrice > 0;

  if (!ready) {
    return (
      <div className="mt-2 rounded border border-(--border) bg-(--bg-base) p-2 text-[10px] text-(--text-muted)">
        先在上方填「股數」與「成交價（買進）」，這裡就會用同一組數字算賣出損益。
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center justify-between gap-2 text-[10px] text-(--text-muted)">
        <span>
          沿用上方：<span className="tnum text-(--text-secondary)">{isTW ? `${(shares / 1000).toFixed(shares % 1000 === 0 ? 0 : 1)} 張` : `${shares} 股`}</span>
          {' '}· 買進 <span className="tnum text-(--text-secondary)">{entryPrice.toFixed(2)}</span>
        </span>
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
        <NumInput
          label="賣出價（試算）"
          value={exitPrice}
          step={priceStep}
          min={0}
          decimals={2}
          onChange={setExitPrice}
          hint={hintPrice.toFixed(2)}
          onHintClick={() => setExitPrice(Math.round(hintPrice * 100) / 100)}
        />
        <div className="rounded border border-(--border) bg-(--bg-base) px-2 py-1 text-right min-w-28">
          <div className="text-[9px] text-(--text-muted)">淨損益（含費{isTW ? '+稅' : '+SEC'}）</div>
          <div className={`text-base font-black tnum ${profitable ? 'text-(--up)' : 'text-(--down)'}`}>
            {profitable ? '+' : ''}{fmtMoney(netPnl, isTW)}
          </div>
        </div>
      </div>

      {tradePlan && (
        <div className="flex items-center gap-1 flex-wrap text-[10px]">
          <span className="text-(--text-muted)">賣在：</span>
          <button type="button" onClick={() => setExitPrice(tradePlan.target1)} className="px-1.5 py-0.5 rounded border border-(--up)/40 text-(--up) hover:bg-(--up)/10">T1 {tradePlan.target1.toFixed(2)}</button>
          <button type="button" onClick={() => setExitPrice(tradePlan.target2)} className="px-1.5 py-0.5 rounded border border-(--up)/40 text-(--up) hover:bg-(--up)/10">T2 {tradePlan.target2.toFixed(2)}</button>
          <button type="button" onClick={() => setExitPrice(tradePlan.stopLoss)} className="px-1.5 py-0.5 rounded border border-(--warn)/40 text-(--warn) hover:bg-(--warn)/10">停損 {tradePlan.stopLoss.toFixed(2)}</button>
        </div>
      )}

      <div className="flex items-center justify-between text-[10px]">
        <span className="text-(--text-muted)">淨報酬率</span>
        <span className={`tnum font-semibold ${profitable ? 'text-(--up)' : 'text-(--down)'}`}>
          {profitable ? '+' : ''}{netPct.toFixed(2)}%
        </span>
        <span className="text-(--text-muted)">損益兩平賣價</span>
        <span className="tnum text-(--text-secondary)">{breakEvenExit != null ? breakEvenExit.toFixed(2) : '—'}</span>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-(--text-muted)">
        <span>買進成本</span><span className="tnum text-right">{fmtMoney(buyCost, isTW)}</span>
        <span>賣出淨收</span><span className="tnum text-right">{fmtMoney(sellProceeds, isTW)}</span>
        <span>買進手續費</span><span className="tnum text-right">{fmtMoney(fees.buyFee, isTW)}</span>
        <span>賣出手續費</span><span className="tnum text-right">{fmtMoney(fees.sellFee, isTW)}</span>
        {isTW ? (
          <><span>證交稅（{isEtf ? '0.1%' : '0.3%'}）</span><span className="tnum text-right">{fmtMoney(fees.levy, isTW)}</span></>
        ) : (
          <><span>SEC + TAF 賣出徵費</span><span className="tnum text-right">{fmtMoney(fees.levy, isTW)}</span></>
        )}
        <span className="text-(--text-secondary)">總費用合計</span>
        <span className="tnum text-right text-(--text-secondary)">{fmtMoney(fees.total, isTW)}</span>
      </div>

      <p className="text-[9px] text-(--text-muted) leading-relaxed">
        {isTW
          ? '台股：買/賣 0.1425%（不折讓、最低 20）+ 賣出證交稅 0.3%（ETF 0.1%）'
          : '美股：買/賣 0.08% + 賣出徵費（SEC/TAF）0.00206%'}
      </p>
    </div>
  );
}

function NumInput({ label, value, step, min, onChange, decimals = 0, suffix, hint, onHintClick }: {
  label: string;
  value: number;
  step: number;
  min?: number;
  onChange: (v: number) => void;
  decimals?: number;
  suffix?: string;
  hint?: string;
  onHintClick?: () => void;
}) {
  const dec = (n: number) => onChange(Math.max(min ?? -Infinity, Number((n - step).toFixed(decimals || 4))));
  const inc = (n: number) => onChange(Number((n + step).toFixed(decimals || 4)));
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[10px] text-(--text-muted) flex items-center justify-between gap-1">
        <span>{label}</span>
        {hint && (
          <button type="button" onClick={onHintClick} className="text-[9px] text-(--accent) hover:underline">{hint}</button>
        )}
      </label>
      <div className="flex items-stretch gap-0.5">
        <button type="button" onClick={() => dec(value)} className="px-1.5 rounded-l border border-(--border) bg-(--bg-base) hover:bg-(--bg-soft)" aria-label="減少">
          <Minus size={12} />
        </button>
        <input
          type="number"
          value={Number.isFinite(value) ? value : 0}
          step={step}
          min={min}
          aria-label={label}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="w-full text-center text-sm tnum border-y border-(--border) bg-(--bg-base) text-(--text-primary) focus:border-(--accent) outline-none px-2"
        />
        <button type="button" onClick={() => inc(value)} className="px-1.5 rounded-r border border-(--border) bg-(--bg-base) hover:bg-(--bg-soft)" aria-label="增加">
          <Plus size={12} />
        </button>
      </div>
      {suffix && <span className="text-[9px] text-(--text-muted) text-right tnum">{suffix}</span>}
    </div>
  );
}
